import {ElementRef, Injectable, signal} from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class VideoPlayerIntegrationService {
  // Video state signals
  public videoCurrentTime = signal(0);
  public videoDuration = signal(0);
  public videoVolume = signal(0); // Default to 0% volume (silent on load)
  public isVideoMuted = signal(true); // Start as muted
  public isVideoPaused = signal(true); // Start as paused until video starts playing

  // Video progress animation tracking
  private videoProgressAnimationFrame?: number;

  constructor() {
  }

  /**
   * Toggle video play/pause state
   */
  toggleVideoPlayPause(videoRef?: ElementRef<HTMLVideoElement>): void {
    const video = videoRef?.nativeElement;
    if (!video) return;

    if (video.paused) {
      video.play().then(() => {
        this.isVideoPaused.set(false);
      }).catch((error) => {
        console.error('Error playing video:', error);
        this.isVideoPaused.set(true);
      });
    } else {
      video.pause();
      this.isVideoPaused.set(true);
    }
  }

  /**
   * Handle video time update event
   */
  onVideoTimeUpdate(): void {
    // Keep this for compatibility but don't update directly
    // The smooth update happens in updateVideoProgress()
  }

  /**
   * Handle video metadata loaded event
   */
  onVideoLoadedMetadata(): void {
    // No event, so can't update videoDuration directly here
    // If needed, update state via VideoPlayer outputs
  }

  /**
   * Handle video play event
   */
  onVideoPlay(): void {
    this.isVideoPaused.set(false);
    this.startSmoothVideoProgress();
  }

  /**
   * Handle video pause event
   */
  onVideoPause(): void {
    this.isVideoPaused.set(true);
    this.stopSmoothVideoProgress();
  }

  /**
   * Handle video ended event
   */
  onVideoEnded(
    onVideoEndedCallback: () => void
  ): void {
    this.stopSmoothVideoProgress();
    onVideoEndedCallback();
  }

  /**
   * Handle video progress click (seek)
   */
  onVideoProgressClick(event: MouseEvent, videoRef?: ElementRef<HTMLVideoElement>): void {
    const video = videoRef?.nativeElement;
    if (!video) return;

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * video.duration;

    video.currentTime = newTime;
    this.videoCurrentTime.set(newTime);

    // Restart smooth progress if video is playing
    if (!video.paused) {
      this.startSmoothVideoProgress();
    }
  }

  /**
   * Update video volume
   */
  updateVideoVolume(volume: number, videoRef?: ElementRef<HTMLVideoElement>): void {
    const video = videoRef?.nativeElement;
    if (!video) return;

    video.volume = volume;
    this.videoVolume.set(volume);
    this.isVideoMuted.set(volume === 0);
  }

  /**
   * Toggle video mute state
   */
  toggleVideoMute(videoRef?: ElementRef<HTMLVideoElement>): void {
    const video = videoRef?.nativeElement;
    if (!video) return;

    video.muted = !video.muted;
    this.isVideoMuted.set(video.muted);
  }

  /**
   * Handle video mouse enter event
   */
  onVideoMouseEnter(): void {
    // Video controls are always visible, no need to change state
  }

  /**
   * Handle video mouse leave event
   */
  onVideoMouseLeave(): void {
    // Video controls are always visible, no need to change state
  }

  /**
   * Handle video volume change event
   */
  onVideoVolumeChange(volume: number, videoRef?: ElementRef<HTMLVideoElement>): void {
    this.updateVideoVolume(volume, videoRef);
  }

  /**
   * Format time in MM:SS format
   */
  formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Reset video state to initial values
   */
  resetVideoState(): void {
    this.stopSmoothVideoProgress();
    this.videoCurrentTime.set(0);
    this.videoDuration.set(0);
    this.videoVolume.set(0); // Default to 0% volume (silent on load)
    this.isVideoMuted.set(true); // Start as muted
    this.isVideoPaused.set(true);
  }

  /**
   * Start smooth video progress tracking
   */
  startSmoothVideoProgress(videoRef?: ElementRef<HTMLVideoElement>): void {
    this.stopSmoothVideoProgress(); // Clean up any existing animation
    this.updateVideoProgress(videoRef);
  }

  /**
   * Stop smooth video progress tracking
   */
  stopSmoothVideoProgress(): void {
    if (this.videoProgressAnimationFrame) {
      cancelAnimationFrame(this.videoProgressAnimationFrame);
      this.videoProgressAnimationFrame = undefined;
    }
  }

  /**
   * Get computed video progress percentage
   */
  getVideoProgressPercentage(): number {
    const duration = this.videoDuration();
    const currentTime = this.videoCurrentTime();
    return duration > 0 ? (currentTime / duration) * 100 : 0;
  }

  /**
   * Cleanup method to clear animations and intervals
   */
  cleanup(): void {
    this.stopSmoothVideoProgress();
  }

  /**
   * Update video progress using requestAnimationFrame for smooth updates
   */
  private updateVideoProgress(videoRef?: ElementRef<HTMLVideoElement>): void {
    const video = videoRef?.nativeElement;
    if (video && !video.paused && !video.ended) {
      this.videoCurrentTime.set(video.currentTime);
      this.videoProgressAnimationFrame = requestAnimationFrame(() => this.updateVideoProgress(videoRef));
    }
  }
}
